import {
  AdvancedMarker,
  InfoWindow,
  useAdvancedMarkerRef,
} from "@vis.gl/react-google-maps";
import { Building, MapPin, X, Zap } from "lucide-react";
import { useCallback, useState } from "react";

import { cn } from "@/lib/utils";
import { AppMapInfo } from "components/AppMap/AppMapInfo";
import { PIN_COLORS } from "helpers/mapColors";

const ICON_BY_TYPE = {
  event: Zap,
  address: Building,
  location: MapPin,
};

// Severity/type color is data-driven, so the pin body and its pointer tip take
// an inline hex rather than a theme token.
function MarkerPin({ color, count, type, selected }) {
  const Icon = selected ? X : (ICON_BY_TYPE[type] ?? MapPin);

  return (
    <div className="tw:flex tw:flex-col tw:items-center tw:drop-shadow-md">
      <div
        className={cn(
          "tw:flex tw:size-8 tw:items-center tw:justify-center tw:rounded-full tw:text-white tw:ring-2 tw:ring-white tw:transition-transform tw:hover:scale-110",
          selected && "tw:scale-110"
        )}
        style={{ backgroundColor: color }}
      >
        {count > 1 ? (
          <span className="tw:text-xs tw:font-semibold">
            {selected ? <Icon className="tw:size-4" /> : count}
          </span>
        ) : (
          <Icon className="tw:size-4" />
        )}
      </div>
      <div
        className="tw:-mt-px tw:size-0 tw:border-x-[5px] tw:border-t-[7px] tw:border-x-transparent"
        style={{ borderTopColor: color }}
      />
    </div>
  );
}

export default function AppMapMarker({ markerData, zIndex }) {
  const [markerRef, marker] = useAdvancedMarkerRef();
  const [infoWindowShown, setInfoWindowShown] = useState(false);

  const handleMarkerClick = useCallback(
    () => setInfoWindowShown(isShown => !isShown),
    []
  );

  const color = PIN_COLORS[markerData.color] ?? PIN_COLORS.grey;

  return (
    <>
      <AdvancedMarker
        ref={markerRef}
        position={markerData.position}
        title={markerData.title}
        zIndex={zIndex}
        onClick={handleMarkerClick}
      >
        <MarkerPin
          color={color}
          count={markerData.count}
          type={markerData.type}
          selected={infoWindowShown}
        />
      </AdvancedMarker>
      {infoWindowShown && (
        <InfoWindow
          anchor={marker}
          maxWidth={340}
          onCloseClick={() => setInfoWindowShown(false)}
          headerDisabled
        >
          <AppMapInfo marker={markerData} />
        </InfoWindow>
      )}
    </>
  );
}
